import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useExpenseData } from '../hooks/useExpenseData';
import { useExpenseForm } from '../hooks/useExpenseForm';
import { uploadExpenseAttachment } from '../../../api/api';
import { useAppSelector } from '../../../redux/hooks';
import { ExpenseDataProviderProps, ExpenseFile, FetchDataType, FETCH_DATA_TYPES } from '../types';

/**
 * ExpenseDataProvider Component
 *
 * Centralized data provider that manages all expense form state and data fetching.
 * Uses render props pattern to provide data and handlers to child components.
 *
 * Responsibilities:
 * - Manages all hooks for data fetching (useExpenseData)
 * - Manages form state and logic (useExpenseForm)
 * - Manages file uploads
 * - Provides unified interface to child components
 */
export const ExpenseDataProvider: React.FC<ExpenseDataProviderProps> = ({
  accessToken,
  clientId,
  profile,
  expenseSettings,
  recordToEdit,
  isMyExpense,
  isPrivateCard,
  updateRecordToEdit,
  children,
}) => {
  // Use the expense form hook first to get status
  const {
    form,
    submitting,
    approving,
    formChanged,
    headerId,
    status,
    createExpense,
    updateExpense,
    approveExpense,
    setInitialForm,
    setApproving,
    setFormChanged,
  } = useExpenseForm({
    clientId,
    profile,
    expenseSettings,
    recordToEdit,
    isMyExpense,
    isPrivateCard,
    updateRecordToEdit,
  });

  // Use the custom hook for all data fetching
  const {
    initializationLoading,
    dimensionsData,
    projectsData,
    costTypesData,
    companyCardsData,
    xglData,
    usersData,
  } = useExpenseData({
    accessToken,
    status,
    recordToEdit,
    clientId,
  });

  // File management state
  const accessTokenForFiles = useAppSelector((state) => state.auth.accessToken);
  const [files, setFiles] = useState<ExpenseFile[]>([]);

  // Initialize form values when component mounts
  useEffect(() => {
    setInitialForm();
  }, [setInitialForm]);

  // Upload files
  const uploadFiles = useCallback(
    (id?: string) => {
      files.forEach(async (file, index) => {
        if (file.file) {
          file.fileList = [
            {
              uid: file.id,
              name: file.file.name,
              status: 'uploading',
            },
          ];

          try {
            const uploadedFile = await uploadExpenseAttachment(
              accessTokenForFiles,
              id ?? headerId,
              file.id,
              file.file
            );

            form.setFieldValue(
              ['expenses', index, 'file_path'],
              uploadedFile.data.file_path
            );

            // Update file list with uploaded file data
            const updatedFiles = [...files];
            updatedFiles[index] = {
              ...updatedFiles[index],
              id: uploadedFile.data.id,
              file: null,
              fileList: [
                {
                  uid: uploadedFile.data.id,
                  name: uploadedFile.data.file_path.split('/').pop(),
                  status: 'done',
                  url: uploadedFile.data.file_path,
                },
              ],
            };
            setFiles(updatedFiles);
          } catch (error) {
            console.error('File upload error:', error);
            // Set file status to error
            file.fileList = [
              {
                uid: file.id,
                name: file.file.name,
                status: 'error',
              },
            ];
          }
        }
      });
    },
    [files, accessTokenForFiles, headerId, form]
  );

  // Memoized wrapper functions to integrate hooks
  const handleCreateExpense = useCallback(async (): Promise<void> => {
    try {
      await createExpense(uploadFiles);
    } catch (error) {
      console.error('Error creating expense:', error);
    }
  }, [createExpense, uploadFiles]);

  const handleUpdateExpense = useCallback(async (): Promise<void> => {
    try {
      await updateExpense(uploadFiles);
    } catch (error) {
      console.error('Error updating expense:', error);
    }
  }, [updateExpense, uploadFiles]);

  const handleApproveExpense = useCallback(async (): Promise<void> => {
    try {
      await approveExpense(files, uploadFiles);
    } catch (error) {
      console.error('Error approving expense:', error);
    }
  }, [approveExpense, files, uploadFiles]);

  // Memoize the render props object to prevent unnecessary re-renders
  const expenseState = useMemo(
    () => ({
      // Form management
      form,
      submitting,
      approving,
      formChanged,
      headerId,
      status,
      setFormChanged,
      setApproving,
      setInitialForm,
      initializationLoading,

      // Data and loading states (using new grouped structure)
      dimensions: dimensionsData,
      projects: projectsData,
      costTypes: costTypesData,
      companyCards: companyCardsData,
      xgl: xglData,
      users: usersData,

      // Universal search handler
      debouncedFetch: (dataType: FetchDataType, params: API.FetchParams) => {
        switch (dataType) {
          case FETCH_DATA_TYPES.DIMENSIONS:
            return dimensionsData.onSearch?.(params);
          case FETCH_DATA_TYPES.PROJECTS:
            return projectsData.onSearch?.(params);
          case FETCH_DATA_TYPES.COST_TYPES:
            return costTypesData.onSearch?.(params);
          case FETCH_DATA_TYPES.COMPANY_CARDS:
            return companyCardsData.onSearch?.(params);
          case FETCH_DATA_TYPES.XGL:
            return xglData.onSearch?.(params);
          default:
            console.warn(`Unknown data type: ${dataType}`);
        }
      },

      // File management
      files,
      setFiles,

      // Action handlers
      handleCreateExpense,
      handleUpdateExpense,
      handleApproveExpense,
    }),
    [
      form,
      submitting,
      approving,
      formChanged,
      headerId,
      status,
      setFormChanged,
      setApproving,
      setInitialForm,
      initializationLoading,
      dimensionsData,
      projectsData,
      costTypesData,
      companyCardsData,
      xglData,
      usersData,
      files,
      setFiles,
      handleCreateExpense,
      handleUpdateExpense,
      handleApproveExpense,
    ]
  );

  // Provide all data and handlers to children
  return children(expenseState);
};
